import { LegalClientFinancialMonitoringEntity } from 'api/types/entity';
import { yesOrNotOption } from 'helpers';
import { TFunc } from 'libs/i18n';

import { CompanyGlobalFinancialMonitoringFormType } from '../../forms/companyGlobalFinancialMonitoringForm';

export const parseGlobalFinancialMonitoringValue = (
  t: TFunc,
  data?: LegalClientFinancialMonitoringEntity
): CompanyGlobalFinancialMonitoringFormType['globalFinancialMonitoring'] => ({
  isSubjectToRegulation: yesOrNotOption(t, data?.isSubjectToRegulation),
  supervisoryAuthorityNameAndLocation:
    data?.globalInfo?.partOne?.supervisoryAuthorityNameAndLocation,
  mandatoryAmlRegulation: data?.globalInfo?.partOne?.mandatoryAmlRegulation && {
    hasMandatoryAmlRegulations: yesOrNotOption(
      t,
      data?.globalInfo.partOne.mandatoryAmlRegulation.hasMandatoryAmlRegulations
    ),
    regulationDetails:
      data?.globalInfo.partOne.mandatoryAmlRegulation.regulationDetails,
    authorizedBodyName:
      data?.globalInfo.partOne.mandatoryAmlRegulation.authorizedBodyName,
  },
  lastAmlAuditResult: data?.globalInfo?.partOne?.lastAmlAuditResult,
  investigationsOrSanctions: data?.globalInfo?.partOne
    ?.investigationsOrSanctions && {
    hadInvestigationsOrSanctions: yesOrNotOption(
      t,
      data?.globalInfo.partOne.investigationsOrSanctions
        .hadInvestigationsOrSanctions
    ),
    amlDecisionDateAndAuthorityInfo:
      data?.globalInfo.partOne.investigationsOrSanctions
        .amlDecisionDateAndAuthorityInfo,
  },
  internalAmlDocuments: data?.globalInfo?.partOne?.internalAmlDocuments && {
    hasInternalAmlDocuments: yesOrNotOption(
      t,
      data?.globalInfo.partOne.internalAmlDocuments.hasInternalAmlDocuments
    ),
    adoptionDate: data?.globalInfo.partOne.internalAmlDocuments.adoptionDate,
    lastUpdateDate:
      data?.globalInfo.partOne.internalAmlDocuments.lastUpdateDate,
    hasEffectivenessAssessmentProcedures: yesOrNotOption(
      t,
      data?.globalInfo.partOne.internalAmlDocuments
        .hasEffectivenessAssessmentProcedures
    ),
    lastEffectivenessCheckInfo:
      data?.globalInfo.partOne.internalAmlDocuments.lastEffectivenessCheckInfo,
    appliesToForeignAffiliates: yesOrNotOption(
      t,
      data?.globalInfo.partOne.internalAmlDocuments.appliesToForeignAffiliates
    ),
  },
  departmentsResponsibleForAmlFunctions: data?.globalInfo?.partOne
    ?.departmentsResponsibleForAmlFunctions && {
    hasDepartmentsResponsibleForAmlFunctions: yesOrNotOption(
      t,
      data?.globalInfo.partOne.departmentsResponsibleForAmlFunctions
        .hasDepartmentsResponsibleForAmlFunctions
    ),
    amlFunctionDepartmentsNames:
      data?.globalInfo.partOne.departmentsResponsibleForAmlFunctions
        .amlFunctionDepartmentsNames,
  },
  responsibleAmlManager: data?.globalInfo?.partOne?.responsibleAmlManager && {
    hasResponsibleAmlManager: yesOrNotOption(
      t,
      data?.globalInfo.partOne.responsibleAmlManager.hasResponsibleAmlManager
    ),
    fullName: data?.globalInfo.partOne.responsibleAmlManager.fullName,
    position: data?.globalInfo.partOne.responsibleAmlManager.position,
    email: data?.globalInfo.partOne.responsibleAmlManager.email,
    phoneNumber: data?.globalInfo.partOne.responsibleAmlManager.phoneNumber,
  },
  hasRiskManagementProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partOne?.hasRiskManagementProcedures
  ),

  hasSourceOfFundsProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasSourceOfFundsProcedures
  )!,

  hasRelationshipsWithPEPProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasRelationshipsWithPEPProcedures
  )!,

  hasAnonymousAccounts: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasAnonymousAccounts
  )!,
  hasAnonymousAccountPreventionProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasAnonymousAccountPreventionProcedures
  )!,
  hasTransactionMonitoringProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasTransactionMonitoringProcedures
  )!,
  hasAssetFreezingProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasAssetFreezingProcedures
  )!,

  hasAMLTrainingProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasAMLTrainingProcedures
  )!,
  amlTrainingDetails: data?.globalInfo?.partTwo?.amlTrainingDetails,

  hasBranchesInNonFATFCountries: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasBranchesInNonFATFCountries
  )!,
  nonFATFBranchesNamesAndLocations:
    data?.globalInfo?.partTwo?.nonFATFBranchesNamesAndLocations,

  hasCorrespondentsInNonFATFCountries: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasCorrespondentsInNonFATFCountries
  )!,
  nonFATFCorrespondentsNames:
    data?.globalInfo?.partTwo?.nonFATFCorrespondentsNames,

  hasCorrespondentsInOffshoreZones: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasCorrespondentsInOffshoreZones
  )!,
  offshoreCorrespondentsNames:
    data?.globalInfo?.partTwo?.offshoreCorrespondentsNames,

  hasShellBankRelationships: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasShellBankRelationships
  )!,
  shellBanksNames: data?.globalInfo?.partTwo?.shellBanksNames,

  hasShellBankPreventionProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasShellBankPreventionProcedures
  )!,
  hasCDDRequestComplianceProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasCDDRequestComplianceProcedures
  )!,

  creditRating: data?.globalInfo?.partTwo?.creditRating,
  subsidiariesNamesAndLocations:
    data?.globalInfo?.partTwo?.subsidiariesNamesAndLocations,
  keyPartnersNames: data?.globalInfo?.partTwo?.keyPartnersNames,
  foreignBranchesNamesAndLocations:
    data?.globalInfo?.partTwo?.foreignBranchesNamesAndLocations,
  financialGroupInfo: data?.globalInfo?.partTwo?.financialGroupInfo,

  hasSanctionPolicyDocuments: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasSanctionPolicyDocuments
  )!,
  hasSanctionsMonitoringProcedures: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.hasSanctionsMonitoringProcedures
  )!,
  isUnderSanctionsOrControlledBySanctionedEntity: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.isUnderSanctionsOrControlledBySanctionedEntity
  )!,
  isListedInSanctionLists: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.isListedInSanctionLists
  )!,
  isLinkedToSanctionedEntities: yesOrNotOption(
    t,
    data?.globalInfo?.partTwo?.isLinkedToSanctionedEntities
  )!,
});
